import { create } from 'zustand';
import { tasksService } from '../services/tasksService';
import type { DailyTasksResponse, TaskItemDto, TaskSectionDto } from '../types/task';

// A local sent mark lasts only while the server still lists the relance as active at the same amount,
// i.e. until the send is recorded and it moves to `sentRelances`.
function stillAwaitingServerRecord(
  previous: DailyTasksResponse | null,
  fresh: DailyTasksResponse,
  completedTaskIds: string[],
): string[] {
  const totalDueById = (response: DailyTasksResponse | null) =>
    new Map((response?.sections ?? []).flatMap(s => s.items).map(i => [i.id, i.totalDue]));
  const before = totalDueById(previous);
  const now = totalDueById(fresh);
  return completedTaskIds.filter(id => now.has(id) && (!before.has(id) || before.get(id) === now.get(id)));
}

/**
 * Task-type-agnostic store for the daily task list. It knows about the generic list, selection,
 * optimistic sent marks, and loading/error — but nothing about any specific task type. Type-specific
 * behaviour (e.g. relance deactivation/snooze) lives in its own store and coordinates through the
 * public `removeTask` / `fetchDailyTasks` actions.
 */
interface TasksState {
  dailyTasks: DailyTasksResponse | null;
  activeTaskCount: number | null;
  selectedTask: TaskItemDto | null;
  completedTaskIds: string[];
  loading: boolean;
  error: string | null;

  fetchDailyTasks: (opts?: { force?: boolean }) => Promise<void>;
  fetchTaskById: (taskId: string) => Promise<void>;
  markTaskCompleted: (taskId: string) => void;
  removeTask: (taskId: string) => void;
  clearTasks: () => void;
}

// Module-level (not store state): Header and Home both call fetchDailyTasks() on mount, so a cold
// load of Home would otherwise fire /tasks/daily twice in parallel, with the second response
// overwriting the first in the store. Concurrent callers share this one in-flight request instead —
// but only when they're fine with whatever is already in flight. A caller that specifically needs
// data newer than "now" (a tenant switch, a reactivation) must pass `force: true` to start its own
// request instead of awaiting a stale one; see `clearTasks` and `relanceDeactivationStore.reactivateTask`.
let inFlightFetch: Promise<void> | null = null;

export const useTasksStore = create<TasksState>((set, get) => ({
  dailyTasks: null,
  activeTaskCount: null,
  selectedTask: null,
  completedTaskIds: [],
  loading: false,
  error: null,

  fetchDailyTasks: (opts) => {
    if (inFlightFetch && !opts?.force) return inFlightFetch;

    set({ loading: true, error: null });
    inFlightFetch = (async () => {
      try {
        const fresh = await tasksService.getDailyTasks();
        const completedTaskIds = stillAwaitingServerRecord(get().dailyTasks, fresh, get().completedTaskIds);
        const activeTaskCount = fresh.sections.reduce((sum, s) => sum + s.totalCount, 0);
        set({ dailyTasks: fresh, activeTaskCount, loading: false, completedTaskIds });
      } catch (e) {
        set({ error: (e as Error).message, loading: false });
      } finally {
        inFlightFetch = null;
      }
    })();
    return inFlightFetch;
  },

  fetchTaskById: async (taskId: string) => {
    set({ loading: true, error: null });
    try {
      const selectedTask = await tasksService.getTaskById(taskId);
      set({ selectedTask, loading: false });
    } catch (e) {
      set({ error: (e as Error).message, loading: false });
    }
  },

  // Generic primitive: drop a task from the active and sent lists, leaving empty
  // sections pruned. Decrements the owning section's totalCount (and activeTaskCount, which sums
  // those counts) so the badge and greeting stay in step with the filtered-out item. Type-specific
  // stores call this after their own side effects (e.g. the relance store after a successful
  // deactivation) so this store needs no knowledge of why.
  removeTask: (taskId: string) => {
    const { dailyTasks, activeTaskCount, completedTaskIds } = get();
    if (!dailyTasks) return;
    const removedFromActive = dailyTasks.sections.some(s => s.items.some(i => i.id === taskId));
    const sections = dailyTasks.sections
      .map(s => {
        const items = s.items.filter(i => i.id !== taskId);
        const removed = items.length !== s.items.length;
        return { ...s, items, totalCount: removed ? Math.max(0, s.totalCount - 1) : s.totalCount };
      })
      .filter(s => s.items.length > 0) as TaskSectionDto[];
    const sentRelances = dailyTasks.sentRelances?.filter(i => i.id !== taskId);
    set({
      dailyTasks: { ...dailyTasks, sections, sentRelances },
      activeTaskCount: removedFromActive && activeTaskCount != null
        ? Math.max(0, activeTaskCount - 1)
        : activeTaskCount,
      completedTaskIds: completedTaskIds.filter(id => id !== taskId),
    });
  },

  clearTasks: () => {
    // A tenant switch must not let a stale request from the previous tenant resolve into this one's
    // store — drop the reference so the next fetchDailyTasks() (Home/Header's mount effect) starts a
    // real request instead of awaiting the old tenant's in-flight promise.
    inFlightFetch = null;
    set({
      dailyTasks: null,
      activeTaskCount: null,
      selectedTask: null,
      completedTaskIds: [],
      error: null,
      loading: true,
    });
  },

  markTaskCompleted: (taskId: string) => {
    const { dailyTasks, completedTaskIds } = get();
    if (!dailyTasks || completedTaskIds.includes(taskId)) return;
    set({ completedTaskIds: [...completedTaskIds, taskId] });
  },
}));
